/**
 * MobileControls - Touch-based controls for mobile devices
 * Left side: Virtual joystick for movement
 * Right side: Touch drag for camera look
 */

export class MobileControls {
  constructor(camera) {
    this.camera = camera;
    this.enabled = false;

    // Joystick state
    this.joystick = {
      active: false,
      touchId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      deltaX: 0,
      deltaY: 0,
      maxRadius: 50 // Maximum joystick displacement in pixels
    };

    // Look control state (now velocity-based like movement)
    this.look = {
      active: false,
      touchId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      deltaX: 0,
      deltaY: 0,
      maxRadius: 50, // Maximum joystick displacement
      rotationSpeed: 2.0 // Rotation speed in radians/second when at max displacement
    };

    // Movement vector (normalized -1 to 1)
    this.moveX = 0;
    this.moveZ = 0;

    // Look velocity (normalized -1 to 1)
    this.lookVelocityX = 0;
    this.lookVelocityY = 0;

    // DOM elements
    this.container = null;
    this.joystickBase = null;
    this.joystickKnob = null;
    this.lookJoystickBase = null;
    this.lookJoystickKnob = null;

    this.createControls();
    this.setupEventListeners();
  }

  createControls() {
    // Create container
    this.container = document.createElement('div');
    this.container.id = 'mobile-controls';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 100;
      display: none;
    `;

    // Create joystick base (LEFT - MOVEMENT)
    this.joystickBase = document.createElement('div');
    this.joystickBase.id = 'movement-joystick';
    this.joystickBase.className = 'joystick-base movement-joystick';
    this.joystickBase.style.cssText = `
      position: absolute;
      bottom: 60px;
      left: 60px;
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(255, 255, 255, 0.3);
      pointer-events: auto;
      touch-action: none;
      opacity: 0.6;
      z-index: 101;
    `;

    // Create joystick knob
    this.joystickKnob = document.createElement('div');
    this.joystickKnob.className = 'joystick-knob';
    this.joystickKnob.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(255, 255, 255, 0.6);
      transform: translate(-50%, -50%);
      transition: background 0.1s;
    `;

    this.joystickBase.appendChild(this.joystickKnob);

    // Create look joystick (RIGHT - LOOK)
    this.lookJoystickBase = document.createElement('div');
    this.lookJoystickBase.id = 'look-joystick';
    this.lookJoystickBase.className = 'joystick-base look-joystick';
    this.lookJoystickBase.style.cssText = `
      position: absolute;
      bottom: 60px;
      right: 60px;
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(255, 255, 255, 0.3);
      pointer-events: auto;
      touch-action: none;
      opacity: 0.6;
      z-index: 101;
    `;

    // Create look joystick knob
    this.lookJoystickKnob = document.createElement('div');
    this.lookJoystickKnob.className = 'joystick-knob';
    this.lookJoystickKnob.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(255, 255, 255, 0.6);
      transform: translate(-50%, -50%);
      transition: background 0.1s;
    `;

    this.lookJoystickBase.appendChild(this.lookJoystickKnob);

    this.container.appendChild(this.joystickBase);
    this.container.appendChild(this.lookJoystickBase);

    document.body.appendChild(this.container);
  }

  setupEventListeners() {
    // Movement joystick events
    this.joystickBase.addEventListener('touchstart', (e) => this.onJoystickStart(e), { passive: false });
    this.joystickBase.addEventListener('touchmove', (e) => this.onJoystickMove(e), { passive: false });
    this.joystickBase.addEventListener('touchend', (e) => this.onJoystickEnd(e), { passive: false });
    this.joystickBase.addEventListener('touchcancel', (e) => this.onJoystickEnd(e), { passive: false });

    // Look joystick events
    this.lookJoystickBase.addEventListener('touchstart', (e) => this.onLookStart(e), { passive: false });
    this.lookJoystickBase.addEventListener('touchmove', (e) => this.onLookMove(e), { passive: false });
    this.lookJoystickBase.addEventListener('touchend', (e) => this.onLookEnd(e), { passive: false });
    this.lookJoystickBase.addEventListener('touchcancel', (e) => this.onLookEnd(e), { passive: false });
  }

  onJoystickStart(e) {
    e.preventDefault();
    if (this.joystick.active) return;

    const touch = e.touches[0];
    this.joystick.active = true;
    this.joystick.touchId = touch.identifier;

    // Get joystick base position
    const rect = this.joystickBase.getBoundingClientRect();
    this.joystick.startX = rect.left + rect.width / 2;
    this.joystick.startY = rect.top + rect.height / 2;
    this.joystick.currentX = touch.clientX;
    this.joystick.currentY = touch.clientY;

    this.updateJoystick();
  }

  onJoystickMove(e) {
    e.preventDefault();
    if (!this.joystick.active) return;

    for (let touch of e.touches) {
      if (touch.identifier === this.joystick.touchId) {
        this.joystick.currentX = touch.clientX;
        this.joystick.currentY = touch.clientY;
        this.updateJoystick();
        break;
      }
    }
  }

  onJoystickEnd(e) {
    e.preventDefault();
    if (!this.joystick.active) return;

    // Check if the released touch is our joystick touch
    let touchReleased = true;
    for (let touch of e.touches) {
      if (touch.identifier === this.joystick.touchId) {
        touchReleased = false;
        break;
      }
    }

    if (touchReleased) {
      this.joystick.active = false;
      this.joystick.touchId = null;
      this.joystick.deltaX = 0;
      this.joystick.deltaY = 0;
      this.moveX = 0;
      this.moveZ = 0;

      // Reset knob position
      this.joystickKnob.style.transform = 'translate(-50%, -50%)';
    }
  }

  updateJoystick() {
    // Calculate delta from center
    let deltaX = this.joystick.currentX - this.joystick.startX;
    let deltaY = this.joystick.currentY - this.joystick.startY;

    // Limit to max radius
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance > this.joystick.maxRadius) {
      const angle = Math.atan2(deltaY, deltaX);
      deltaX = Math.cos(angle) * this.joystick.maxRadius;
      deltaY = Math.sin(angle) * this.joystick.maxRadius;
    }

    this.joystick.deltaX = deltaX;
    this.joystick.deltaY = deltaY;

    // Normalize to -1 to 1 range
    this.moveX = deltaX / this.joystick.maxRadius;
    this.moveZ = -deltaY / this.joystick.maxRadius; // Invert Y for forward/backward

    // Update knob visual position
    this.joystickKnob.style.transform = `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px))`;
  }

  onLookStart(e) {
    e.preventDefault();
    if (this.look.active) return;

    const touch = e.touches[0];
    this.look.active = true;
    this.look.touchId = touch.identifier;

    // Get joystick base position
    const rect = this.lookJoystickBase.getBoundingClientRect();
    this.look.startX = rect.left + rect.width / 2;
    this.look.startY = rect.top + rect.height / 2;
    this.look.currentX = touch.clientX;
    this.look.currentY = touch.clientY;

    this.updateLookJoystick();
  }

  onLookMove(e) {
    e.preventDefault();
    if (!this.look.active) return;

    for (let touch of e.touches) {
      if (touch.identifier === this.look.touchId) {
        this.look.currentX = touch.clientX;
        this.look.currentY = touch.clientY;
        this.updateLookJoystick();
        break;
      }
    }
  }

  updateLookJoystick() {
    // Calculate delta from center
    let deltaX = this.look.currentX - this.look.startX;
    let deltaY = this.look.currentY - this.look.startY;

    // Limit to max radius
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance > this.look.maxRadius) {
      const angle = Math.atan2(deltaY, deltaX);
      deltaX = Math.cos(angle) * this.look.maxRadius;
      deltaY = Math.sin(angle) * this.look.maxRadius;
    }

    this.look.deltaX = deltaX;
    this.look.deltaY = deltaY;

    // Normalize to -1 to 1 range for velocity
    this.lookVelocityX = deltaX / this.look.maxRadius;
    this.lookVelocityY = deltaY / this.look.maxRadius;

    // Update knob visual position
    this.lookJoystickKnob.style.transform = `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px))`;
  }

  onLookEnd(e) {
    e.preventDefault();
    if (!this.look.active) return;

    // Check if the released touch is our look touch
    let touchReleased = true;
    for (let touch of e.touches) {
      if (touch.identifier === this.look.touchId) {
        touchReleased = false;
        break;
      }
    }

    if (touchReleased) {
      this.look.active = false;
      this.look.touchId = null;
      this.look.deltaX = 0;
      this.look.deltaY = 0;
      this.lookVelocityX = 0;
      this.lookVelocityY = 0;

      // Reset look joystick knob position
      this.lookJoystickKnob.style.transform = 'translate(-50%, -50%)';
    }
  }

  enable() {
    this.enabled = true;
    this.container.style.display = 'block';
    console.log('Mobile controls enabled');
  }

  disable() {
    this.enabled = false;
    this.container.style.display = 'none';
    this.joystick.active = false;
    this.look.active = false;
    this.moveX = 0;
    this.moveZ = 0;
    this.lookVelocityX = 0;
    this.lookVelocityY = 0;
    console.log('Mobile controls disabled');
  }

  /**
   * Get movement direction in camera space
   */
  getMovement() {
    return {
      forward: this.moveZ,
      right: this.moveX
    };
  }

  /**
   * Get camera rotation velocity (normalized -1 to 1)
   */
  getRotationVelocity() {
    return {
      x: this.lookVelocityY, // Vertical joystick movement -> pitch (x rotation)
      y: this.lookVelocityX  // Horizontal joystick movement -> yaw (y rotation)
    };
  }

  dispose() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}
